import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useUnit } from "effector-react";

import CorpsDevicesToolbar from "../components/Devices/CorpsDevicesToolbar";
import DevicesTable from "../components/Devices/DevicesTable";
import AddDeviceDialog from "../components/Devices/AddDeviceDialog";
import MeterReadingsImportDialog from "../components/Devices/MeterReadingsImportDialog";

import { reportsApi } from "../api/reports";
import { useBuildingMeta } from "../hooks/useBuildingMeta";
import { buildFileName, downloadBlobAsFile } from "../utils/download";
import {
  $devicesByCorps,
  $devicesTotal,
  $devicesPagination,
  fetchDevicesByCorpsFx,
  dateChanged,
  $devicesDate,
  updateDeviceFx,
} from "../store/devices";
import {
  $corpsByBuilding,
  fetchCorpsByBuildingFx,
} from "../store/buildingCorps";
import { $canDeleteComplex } from "../store/auth";
import "../components/Complexes/ResidentialComplexList.css";

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function CorpsDevicesPage() {
  const { corpsId } = useParams(); // ожидаем маршрут /corps/:corpsId
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();

  const [corpsMap, devicesMap, totalMap, pagMap, date, canManage] = useUnit([
    $corpsByBuilding,
    $devicesByCorps,
    $devicesTotal,
    $devicesPagination,
    $devicesDate,
    $canDeleteComplex,
  ]);

  const buildingId = sp.get("buildingId") || "";
  const page = Number(sp.get("page") || pagMap[corpsId || ""]?.page || 1);
  const pageSize = pagMap[corpsId || ""]?.pageSize || 10;

  const corpsList = corpsMap[String(buildingId)] || [];
  const namedCorps = useMemo(
    () => corpsList.filter((item) => !item.is_default),
    [corpsList]
  );
  const isFlatBuilding = namedCorps.length === 0;
  const items = devicesMap[String(corpsId)] || [];
  const total = totalMap[String(corpsId)] || 0;
  const updateDevice = useUnit(updateDeviceFx);
  const loading = useUnit(fetchDevicesByCorpsFx.pending);
  const { meta: buildingMeta } = useBuildingMeta(buildingId);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersDraft, setFiltersDraft] = useState({
    status: '',
    system_type: '',
    manufacturer: '',
    serial_number: '',
    room_number: '',
    suffix: '',
  });
  const [filters, setFilters] = useState(filtersDraft);
  const [sortBy, setSortBy] = useState('room_number');
  const [sortDir, setSortDir] = useState('asc');

  const handleDeviceUpdate = useCallback(
    (device, changes) => {
      if (!device) return Promise.resolve();
      const targetCorpsId = device.corps_id ?? corpsId;
      return updateDevice({
        id: String(device.id),
        corpsId: String(targetCorpsId ?? ""),
        data: changes,
      });
    },
    [updateDevice, corpsId]
  );

  useEffect(() => {
    if (buildingId) fetchCorpsByBuildingFx(String(buildingId));
  }, [buildingId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(filtersDraft);
    }, 300);
    return () => clearTimeout(t);
  }, [filtersDraft]);

  useEffect(() => {
    if (corpsId)
      fetchDevicesByCorpsFx({
        corpsId: String(corpsId),
        date,
        page,
        pageSize,
        search: searchQuery || undefined,
        filters,
        sort: { sort_by: sortBy, sort_dir: sortDir },
      });
  }, [corpsId, date, page, pageSize, searchQuery, filters, sortBy, sortDir]);

  const handleExport = useCallback(async () => {
    const complexId = buildingMeta?.residential_complex_id;
    const effectiveCorpsId =
      corpsId && uuidRegex.test(String(corpsId)) ? String(corpsId) : undefined;

    if (!complexId) {
      console.warn("Нет идентификатора ЖК для выгрузки отчета");
      return;
    }
    if (!effectiveCorpsId && !buildingId) {
      console.warn("Нет корпуса или дома для выгрузки отчета");
      return;
    }

    try {
      setExporting(true);
      const today = new Date();
      const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const selectedMonth = date ? String(date).slice(0, 7) : '';
      const { data: blob, fileName: serverFileName } =
        await reportsApi.downloadResidentialConsumptionReport({
          residentialComplexId: complexId,
          period: selectedMonth && selectedMonth !== currentMonth ? date : undefined,
          corpsId: effectiveCorpsId,
          buildingId: !effectiveCorpsId && buildingId ? String(buildingId) : undefined,
        });

      const fallback = buildFileName(
        [
          "consumption",
          complexId,
          buildingId ? `building-${buildingId}` : null,
          effectiveCorpsId ? `corps-${effectiveCorpsId}` : null,
          date || null,
        ],
        "consumption-report"
      );

      const finalName =
        typeof serverFileName === "string" && serverFileName.trim().length
          ? serverFileName.trim()
          : `${fallback}.xlsx`;

      downloadBlobAsFile(blob, finalName);
    } catch (error) {
      console.error("Не удалось выгрузить отчет", error);
    } finally {
      setExporting(false);
    }
  }, [buildingMeta, corpsId, date, buildingId]);

  const corpsTabs = useMemo(() => {
    const source = isFlatBuilding ? [] : namedCorps;
    return source.map((c) => ({
      id: c.id,
      name: c.name,
      active: String(c.id) === String(corpsId),
    }));
  }, [isFlatBuilding, namedCorps, corpsId]);

  const hasUrtDevices = useMemo(() => {
    return items.some((item) => item?.system_type === "УРТ");
  }, [items]);

  const handleSearch = useCallback((value) => {
    const next = value || '';
    setSearchQuery(next);
    setSp({ buildingId, page: '1' }, { replace: true });
  }, [buildingId, setSp]);

  const openBuildingView = useCallback((view) => {
    if (!buildingId) return;
    navigate(`/building/${buildingId}/corps?view=${view}`);
  }, [buildingId, navigate]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);

  return (
    <div
      className="home-container"
      style={{ marginLeft: 24, marginRight: 24, overflowX: 'hidden' }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <button className="rc-btn" onClick={() => navigate(-1)}>
          ← Назад
        </button>
        {buildingId ? (
          <div className="rc-corps-mode-switch" aria-label="Переключение вида дома">
            <button type="button" className="rc-corps-mode-switch__btn is-active">
              Приборы
            </button>
            <button
              type="button"
              className="rc-corps-mode-switch__btn"
              onClick={() => openBuildingView('floors')}
            >
              Мнемосхема
            </button>
            <button
              type="button"
              className="rc-corps-mode-switch__btn"
              onClick={() => openBuildingView('channels')}
            >
              Каналы
            </button>
          </div>
        ) : null}
        <div style={{ marginLeft: "auto" }}>
          {canManage && (
            <>
              {hasUrtDevices && (
                <button
                  className="rc-btn"
                  style={{ marginRight: 8 }}
                  onClick={() => setOpenImport(true)}
                >
                  Импорт показаний
                </button>
              )}
              <button className="rc-btn" onClick={() => setOpenAdd(true)}>
                Добавить устройство
              </button>
            </>
          )}
        </div>
      </div>

      <CorpsDevicesToolbar
        date={date || ""}
        onDateChange={(d) => dateChanged(d)}
        corpsTabs={corpsTabs}
        onPickCorps={(id) => navigate(`/corps/${id}?buildingId=${buildingId}`)}
        onSearch={handleSearch}
        onExport={handleExport}
        exportLoading={exporting}
        exportDisabled={!buildingMeta?.residential_complex_id}
        searchValue={searchQuery}
      />

      <DevicesTable
        rows={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        filters={filtersDraft}
        sortBy={sortBy}
        sortDir={sortDir}
        onFiltersChange={(next) => {
          setFiltersDraft(next);
          setSp({ buildingId, page: '1' }, { replace: true });
        }}
        onSortChange={(field) => {
          const nextDir = sortBy === field && sortDir === 'asc' ? 'desc' : 'asc';
          setSortBy(field);
          setSortDir(nextDir);
          setSp({ buildingId, page: '1' }, { replace: true });
        }}
        onPageChange={(p) => {
          setSp({ buildingId, page: String(p) }, { replace: true });
        }}
        onDeviceUpdate={handleDeviceUpdate}
        onDeviceReplaced={() => {
          if (!corpsId) return;
          fetchDevicesByCorpsFx({
            corpsId: String(corpsId),
            date,
            page,
            pageSize,
            search: searchQuery || undefined,
            filters,
            sort: { sort_by: sortBy, sort_dir: sortDir },
          });
        }}
      />

      {canManage && (
        <AddDeviceDialog
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          corpsId={String(corpsId || "")}
          buildingId={String(buildingId || "")}
          onSuccess={() =>
            fetchDevicesByCorpsFx({
              corpsId: String(corpsId),
              date,
              page: 1,
              pageSize,
              filters,
              sort: { sort_by: sortBy, sort_dir: sortDir },
            })
          }
        />
      )}

      {canManage && (
        <MeterReadingsImportDialog
          open={openImport}
          onClose={() => setOpenImport(false)}
          onImported={() => {
            if (corpsId) {
              fetchDevicesByCorpsFx({
                corpsId: String(corpsId),
                date,
                page,
                pageSize,
                search: searchQuery || undefined,
                filters,
                sort: { sort_by: sortBy, sort_dir: sortDir },
              });
            }
          }}
        />
      )}
    </div>
  );
}
